import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import * as icons from "lucide-react";

import Announcements from '../components/lp/Announcements';
import ContactSection from '../components/lp/ContactSection';
import { smoothScrollTo } from '../utils/scroll';
import SEO from '../components/common/SEO';
import lpData from '../data/lp-data.json';

// --- Helper Functions ---
const toPascalCase = (str) => {
  if (!str) return null;
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

const getAlignClass = (align) => {
    switch (align) {
        case 'left':
            return 'text-left';
        case 'right':
            return 'text-right';
        case 'center':
        default:
            return 'text-center';
    }
};

const getJustifyClass = (align) => {
    switch (align) {
        case 'left':
            return 'justify-start';
        case 'right':
            return 'justify-end';
        case 'center':
        default:
            return 'justify-center';
    }
};

const getMarginClass = (align) => {
    switch (align) {
        case 'left':
            return 'mr-auto ml-0';
        case 'right':
            return 'ml-auto mr-0';
        case 'center':
        default:
            return 'mx-auto';
    }
};

// --- Page Section Components ---

const Hero = ({ data }) => {
    const style = data?.backgroundImage
        ? { backgroundImage: `url(${data.backgroundImage})` }
        : { backgroundColor: '#2563EB' }; // blue-600

    const titleAlignClass = getAlignClass(data?.titleAlign);
    const subtitleAlignClass = getAlignClass(data?.subtitleAlign);
    const subtitleMarginClass = getMarginClass(data?.subtitleAlign);
    const ctaJustifyClass = getJustifyClass(data?.ctaAlign);

    // 黒色オーバーレイ濃淡 (% から 0〜1 の opacity)
    const rawOpacity = data?.overlayOpacity !== undefined && data?.overlayOpacity !== '' ? Number(data.overlayOpacity) : 50;
    const overlayOpacityDecimal = Math.min(Math.max(rawOpacity, 0), 100) / 100;

    // PC用縦幅 (px) の指定
    const pcHeightStyle = data?.pcHeight ? { minHeight: `${data.pcHeight}px` } : {};

    return (
        <section
            className="bg-cover bg-center flex items-center"
            style={{ ...style, ...pcHeightStyle }}
        >
            <div
                className="w-full py-24 px-4 flex items-center justify-center min-h-full"
                style={{
                    backgroundColor: data?.backgroundImage
                        ? `rgba(0, 0, 0, ${overlayOpacityDecimal})`
                        : undefined
                }}
            >
                <div className="max-w-4xl mx-auto w-full">
                    <h1
                        className={`text-4xl md:text-5xl font-bold ${titleAlignClass}`}
                        style={{ color: data?.titleColor || '#ffffff' }}
                    >
                        {data?.title || 'Stripe請求書のCSV一括発行・自動化サービス'}
                    </h1>
                    <p
                        className={`mt-4 text-lg md:text-xl max-w-2xl ${subtitleAlignClass} ${subtitleMarginClass}`}
                        style={{ color: data?.subtitleColor || '#ffffff' }}
                    >
                        {data?.subtitle || 'Billwaveは、CSVをアップロードするだけでStripe請求書の一括発行・予約発行を自動化。経理業務の効率化を強力にサポ―トします。'}
                    </p>
                    <div className={`mt-8 flex flex-col sm:flex-row ${ctaJustifyClass} gap-4`}>
                        <Link
                            to="/signup"
                            className="inline-block bg-white font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-colors shadow-lg text-center"
                            style={{ color: data?.ctaTextColor || '#2563eb' }}
                        >
                            {data?.ctaText || '無料で始める'}
                        </Link>
                        <Link
                            to="/support/how-to-use"
                            className="inline-block bg-blue-700 text-white font-bold py-3 px-8 rounded-full hover:bg-blue-800 transition-colors border border-blue-400 text-center"
                        >
                            利用ガイドを見る
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Features = ({ data }) => {
    if (!data) return null;

    return (
        <section id="features" className="py-20 bg-gray-50"> 
            <div className="max-w-6xl mx-auto px-4">
                {/* --- Problems Section --- */}
                <div className="mb-20">
                    <h2 className="text-3xl font-bold text-center mb-4">請求業務でこんなお悩みはありませんか？</h2>
                    <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">日々の請求書作成やStripe手動入力で直面する課題をBillwaveがシンプルに解決します。</p>
                    <div className="grid md:grid-cols-3 gap-8">
                        {data?.problems?.map((item, index) => {
                            const iconName = toPascalCase(item.icon);
                            const Icon = icons[iconName] || icons.AlertCircle;
                            return (
                                <div key={index} className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 flex items-start gap-4">
                                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-blue-50 rounded-full">
                                        <Icon className="w-6 h-6 text-blue-600" aria-label={item.title || '課題アイコン'} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-red-600">{item.title}</h3>
                                        <p className="mt-2 text-gray-600">{item.description}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* --- Solutions Section --- */}
                <div>
                    <h2 className="text-3xl font-bold text-center mb-4">Billwaveが選ばれる理由と主な機能</h2>
                    <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">Stripe API連携により、安全・確実に請求書のCSV一括作成・自動送信を実現します。</p>
                    <div className="grid md:grid-cols-3 gap-8">
                        {data?.solutions?.map((item, index) => {
                            const iconName = toPascalCase(item.icon);
                            const Icon = icons[iconName] || icons.File;
                            return (
                                <div key={index} className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                                     <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                                        <Icon className="w-7 h-7 text-blue-600" aria-label={item.title || '機能アイコン'} />
                                    </div>
                                    <h3 className="font-bold text-lg text-blue-700">{item.title}</h3>
                                    <p className="mt-2 text-gray-600">{item.description}</p>
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Internal link callout */}
                    <div className="mt-12 text-center bg-blue-50 p-6 rounded-xl border border-blue-100">
                        <p className="text-gray-700 mb-3 font-semibold">CSVフォーマットの仕様やアップロード項目の詳細はこちら</p>
                        <Link to="/support/csv-format" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold hover:underline">
                            CSVフォーマット仕様書を見る <icons.ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Flow = ({ data }) => (
    <section id="flow" className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">ご利用の流れ</h2>
            <p className="text-center text-gray-600 mb-12">簡単な設定だけでStripe請求書の一括発行を始められます。</p>
            <div className="space-y-8">
                {data?.steps?.map((step, index) => (
                    <div key={index} className="flex items-start">
                        <div className="bg-blue-600 text-white rounded-full h-10 w-10 flex-shrink-0 flex items-center justify-center font-bold text-lg">{index + 1}</div>
                        <div className="ml-6 bg-gray-50 border border-gray-200 p-6 rounded-lg flex-1">
                            <h3 className="font-bold text-lg">{step.title}</h3>
                            <p className="mt-2 text-gray-600">{step.description}</p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-10 text-center">
                <Link to="/support/how-to-use" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold hover:underline">
                    詳しい利用ガイド・操作手順を見る <icons.ArrowRight className="w-4 h-4 ml-1" />
                </Link>
            </div>
        </div>
    </section>
);

const Pricing = () => {
    const plans = [
        {
            name: "フリー",
            price: "無料",
            priceDetails: "",
            features: ["請求書アップロード (月5件まで)", "予約済み請求書"],
        },
        {
            name: "ライト",
            price: "1,000円",
            priceDetails: "/月",
            overage: "超過料金：100円/件",
            features: ["請求書アップロード (月30件まで)", "予約済み請求書", "顧客アップロード", "顧客削除"],
        },
        {
            name: "スタンダード",
            price: "3,000円",
            priceDetails: "/月",
            overage: "超過料金：60円/件",
            features: ["請求書アップロード (月100件まで)", "予約済み請求書", "顧客アップロード", "顧客削除", "チーム管理"],
        },
        {
            name: "ビジネス",
            price: "8,000円",
            priceDetails: "/月",
            overage: "超過料金：30円/件",
            features: ["請求書アップロード (月300件まで)", "予約済み請求書", "顧客アップロード", "顧客削除", "チーム管理"],
        },
    ];

    return (
        <section id="pricing" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center mb-4">料金プラン</h2>
                <p className="text-center text-gray-600 mb-12">事業規模に合わせて選べるシンプルな料金プラン。フリープランからすぐにお試しいただけます。</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {plans.map((plan) => (
                        <div key={plan.name} className="border rounded-lg p-6 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow">
                            <h3 className="text-2xl font-bold text-center">{plan.name}</h3>
                            <div className="mt-4 text-center text-gray-900">
                                <span className="text-4xl font-extrabold">{plan.price}</span>
                                <span className="text-base font-medium text-gray-500">{plan.priceDetails}</span>
                            </div>
                            <p className="mt-2 text-center text-sm text-gray-500 h-4">{plan.overage || ''}</p>
                            <ul className="mt-6 space-y-4 flex-grow">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <icons.CheckCircle className="h-6 w-6 text-green-500" />
                                        </div>
                                        <p className="ml-3 text-sm text-gray-700">{feature}</p>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8">
                                <Link to="/signup" className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-md transition-colors">
                                    無料で試す
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const FAQ = ({ data }) => (
    <section id="faq" className="py-20 bg-white"> 
        <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">よくある質問</h2>
            <p className="text-center text-gray-600 mb-12">導入前のご不明点や機能についてお答えします。</p>
            <div className="space-y-4">
                {data?.items?.map((item, index) => (
                    <details key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200 cursor-pointer">
                        <summary className="font-semibold text-lg list-none text-gray-900">{item.title}</summary>
                        <p className="mt-4 text-gray-600">{item.description}</p>
                    </details>
                ))}
            </div>
            <div className="mt-10 text-center">
                <Link to="/faq" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold hover:underline">
                    すべてのよくある質問（FAQ）を見る <icons.ArrowRight className="w-4 h-4 ml-1" />
                </Link>
            </div>
        </div>
    </section>
);

const CTA = ({ data }) => {
    return (
        <section className="bg-blue-600 text-white text-center py-20 px-4">
            <h2 className="text-3xl font-bold">{data?.title || 'Stripeからの請求書発行をBillwaveでより簡単便利に'}</h2>
            <p className="mt-4 text-blue-100 max-w-xl mx-auto">フリープランならクレジットカード登録不要で、今すぐ月5件までのCSV一括発行をお試しいただけます。</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <Link to={data?.link || "/signup"} className="bg-white text-blue-600 font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-colors shadow-lg">
                    {data?.buttonText || '無料ではじめる'}
                </Link>
                <Link to="/support/how-to-use" className="bg-blue-700 text-white font-bold py-3 px-8 rounded-full hover:bg-blue-800 transition-colors border border-blue-400">
                    利用ガイドを見る
                </Link>
            </div>
        </section>
    );
};

// --- Main Home Page Component ---

const Home = () => {
    const [announcementsData, setAnnouncementsData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    // --- Anchor Scroll Logic ---
    useEffect(() => {
        const targetId = location.state?.targetId;
        if (targetId) {
            const element = document.getElementById(targetId);
            if (element) {
                const headerOffset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                smoothScrollTo(offsetPosition, 800);
            }
        }
    }, [location.state]);

    // --- Auth and Announcements Fetching Logic ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, user => {
            if (user) {
                navigate('/invoice-upload');
            }
        });

        const fetchAnnouncements = async () => {
            try {
                const announcementsQuery = query(
                    collection(db, 'announcements'), 
                    where('status', '==', 'published'),
                    orderBy('publishedAt', 'desc')
                );
                const announcementsSnapshot = await getDocs(announcementsQuery);
                const announcementsList = announcementsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setAnnouncementsData(announcementsList);
            } catch (err) {
                console.error("Error fetching announcements:", err);
            }
        };
        
        fetchAnnouncements();

        return () => unsubscribe();
    }, [navigate]);

    const topPageJsonLd = [
        {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            'name': 'Billwave',
            'operatingSystem': 'Web Browser',
            'applicationCategory': 'BusinessApplication',
            'offers': {
                '@type': 'AggregateOffer',
                'priceCurrency': 'JPY',
                'lowPrice': '0',
                'highPrice': '8000',
                'offerCount': '4'
            },
            'description': 'Stripeと連携し、CSVアップロードで請求書を一括発行・予約発行できるクラウドサービスです。'
        },
        {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': '株式会社シグナル',
            'url': 'https://s-nal.jp',
            'logo': 'https://billwave.jp/assets/logo.png',
            'sameAs': ['https://billwave.jp']
        },
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': 'Billwave',
            'url': 'https://billwave.jp'
        }
    ];

    if (lpData?.faq?.items?.length > 0) {
        topPageJsonLd.push({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': lpData.faq.items.map(item => ({
                '@type': 'Question',
                'name': item.title,
                'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': item.description
                }
            }))
        });
    }

    return (
        <>
            <SEO 
                title="Stripe請求書のCSV一括発行・自動化サービス | Billwave"
                description="Stripeと連携しCSVをアップロードするだけで請求書を一括発行・予約発行できるクラウドサービス「Billwave」。銀行振込・クレジットカード決済、インボイス制度に対応。経理業務・請求業務の自動化と大幅な効率化を実現します。まずは無料でお試しください。"
                path="/"
                jsonLd={topPageJsonLd}
            />
            <main>
                <Hero data={lpData.hero} />
                <Announcements items={announcementsData} />
                <Features data={lpData.features} />
                <Flow data={lpData.flow} />
                <Pricing />
                <FAQ data={lpData.faq} />
                <CTA data={lpData.cta} />
                <ContactSection />
            </main>
        </>
    );
};

export default Home;
